export default {
  header: {
    about: 'About us',
    services: 'Services',
    projects: 'Projects',
    contact: 'Contact',
    impact: 'Impact',
  },

  about: {
    eyebrow: 'About BlueAlps',
    hero_title_line1: 'About',
    hero_title_line2: 'us',
    hero_title:
      'Swiss-Brazilian technology for companies ready to move forward.',
    hero_tagline:
      'Sustainable technology to reduce inequalities and create positive impact.',
    hero_lead:
      'BlueAlps is a Swiss-Brazilian technology company specializing in custom software development, artificial intelligence, automation, and digital transformation for businesses.',
    cta_services: 'Explore our services',
    cta_contact: 'Talk to us',
    story_label: 'Our story',
    story_title: 'From Swiss-Brazilian roots to a multicultural team.',
    story_title_part1: 'A story built with ',
    story_title_part2: 'purpose and people',
    story_paragraphs: [
      'BlueAlps was born from a dream: to create opportunities through technology and generate positive impact.',
      'What started with a vision and the first developer grew into a multicultural team.',
    ],
    story_highlight:
      'Today, <strong>companies</strong> trust our potential to build innovative and sustainable solutions.',
    story_stat_countries: '<strong>2 countries</strong> connected',
    story_stat_solutions: '<strong>Custom</strong> solutions',
    client_map_title: 'Our main clients',
    client_map_country: 'Switzerland',
    story_quote:
      'Our main differentiator is delivering <strong>Swiss quality</strong> with <strong>one of the most competitive rates in the market</strong>. We combine Brazilian creativity, Swiss precision, and an efficient operation to offer high-performance solutions at a more accessible price.',
    story_steps: [
      {
        title: 'The dream',
        text: 'Technology to create opportunities and positive impact.',
      },
      {
        title: 'The first step',
        text: 'A vision came to life with the first developer.',
      },
      {
        title: 'The team grew',
        text: 'New talents and perspectives joined the journey.',
      },
      {
        title: 'Trust arrived',
        text: 'More than 5 companies started trusting us.',
      },
      {
        title: 'We are just beginning',
        text: 'We keep growing between Brazil and Switzerland.',
      },
    ],
    faq: {
      label: 'FAQ',
      title: 'Frequently asked questions',
      subtitle:
        'Direct answers about how we work, what makes us different, and what we deliver in each project.',
      items: [
        {
          question: 'What is BlueAlps’ main differentiator?',
          answer:
            'We combine Swiss quality, Brazilian creativity, and an efficient operation to deliver high-performance technology with one of the most competitive rates in the market.',
        },
        {
          question: 'Does BlueAlps work with companies outside Switzerland?',
          answer:
            'Yes. We work with companies in Switzerland, Brazil, and other markets through remote communication, clear processes, and structured deliveries.',
        },
        {
          question: 'What type of solutions do you build?',
          answer:
            'We build custom software, automations, AI solutions, integrations, websites, internal systems, and digital transformation projects.',
        },
        {
          question: 'How does a project start?',
          answer:
            'We begin by understanding the goal, current context, and client priorities. Then we propose scope, steps, timeline, and investment transparently.',
        },
      ],
    },
    feedback: {
      label: 'Client feedback',
      title: 'What our clients say',
      subtitle:
        'Partnerships built with clarity, responsibility, and a focus on consistent digital outcomes.',
      items: [
        {
          quote:
            'We are very satisfied with the collaboration. The young, motivated team works in a straightforward, solution-oriented way and with great commitment. Our concerns were always taken seriously and implemented quickly - exactly what you expect from a successful partnership.',
          name: 'Cornelia',
          company: 'Divina Textil AG',
          companyUrl: 'https://www.divina.ch/de',
        },
        {
          quote:
            'The collaboration was very pleasant. When I met Laryssa, it was very reassuring, as communicating in German became much easier and more personal. I especially appreciated the quick responses, the patience, and the team’s support, even after the homepage was already online.',
          name: 'Erika',
          company: 'Müller Project',
          companyUrl: 'https://mueller-handarbeiten.ch/',
        },
        {
          quote:
            'Working with Igor on the further development of HausHeld.ch, Aufzugo, and other projects has always been very professional and reliable. We particularly value his high quality, his fast implementation of feedback, and his solution-oriented way of working. Igor is a valued partner whom we can recommend at any time.',
          name: 'Alessandro Affentranger',
          company: 'Head of Development - Vermando AG',
          companyUrl: 'https://www.vermando.com/',
        },
      ],
    },
    website_carousel: {
      label: 'Our website',
      title: 'Discover what you can find at BlueAlps',
      subtitle:
        'Each area of the website shows how we connect technology, impact, and partnership.',
      items: [
        {
          title: 'Services',
          description:
            'Explore our solutions in software, artificial intelligence, automation, technical SEO, UX, PIM, WordPress, and cybersecurity.',
        },
        {
          title: 'Impact',
          description:
            'Learn how we connect technology and social responsibility to create results aligned with a more sustainable future.',
        },
        {
          title: 'Projects',
          description:
            'See examples of digital deliveries, case studies, and initiatives that show our technical capability in practice.',
        },
        {
          title: 'Contact',
          description:
            'Talk to the BlueAlps team to present your challenge, request a proposal, or start a new partnership.',
        },
        {
          title: 'Values',
          description:
            'Learn about the principles that guide our decisions, our culture, and the way we build trusted relationships.',
        },
      ],
    },
    team: {
      label: 'BlueAlps team',
      title: 'The people behind the technology',
      subtitle:
        'A multicultural group combining strategy, engineering, and execution to turn ideas into digital solutions.',
      linkedin_label: 'View {{name}} on LinkedIn',
      members: [
        {
          name: 'Daniel Hefti',
          role: 'Chief Executive Officer',
          description:
            'Leads BlueAlps strategic vision, connecting the Swiss market with high-quality digital solutions and impact.',
        },
        {
          name: 'Igor Araújo',
          role: 'IT Manager & Full-Stack Developer',
          description:
            'Helps companies create custom features, improve technical SEO, connect APIs, and strengthen cybersecurity for websites and digital platforms.',
        },
        {
          name: 'Maria Victória',
          role: 'IT Manager & Frontend Developer',
          description:
            'Combines IT management, e-commerce, UX, Google tools, and design creation to turn ideas into clear, functional digital experiences.',
        },
        {
          name: 'Matheus Luiz',
          role: 'Backend Developer & DevOps',
          description:
            'Focuses on backend, AI, automation, and integrations for e-commerce and PIM Core, creating smarter flows for data, products, and operations.',
        },
        {
          name: 'Laryssa Sams',
          role: 'Customer Relations',
          description:
            'Supports communication with clients and partners, ensuring clarity, follow-up, and care at every stage.',
        },
      ],
    },
    mission_title: 'Mission',
    mission_text:
      'To support businesses with accessible, high-performance, impact-oriented software solutions that connect technical expertise, business understanding, and social responsibility.',
    vision_title: 'Vision',
    vision_text:
      'To foster a fairer, more inclusive, and more sustainable future through well-built technology, international collaboration, and innovation applied to real challenges.',
    values_label: 'Our values',
    values_title: 'Our values',
    values_intro:
      'Our values are the foundation of our identity and guide every decision we make. They shape our culture, our partnerships, and the impact we want to create.',
    values: {
      equity: {
        title: 'Equity',
        text: 'We promote fair opportunities and outcomes for all people, with fairness in partnerships, processes, and the workplace.',
      },
      inclusion: {
        title: 'Inclusion & Diversity',
        text: 'We create welcoming environments that value different voices, backgrounds, cultures, and experiences.',
      },
      integrity: {
        title: 'Integrity',
        text: 'We act with transparency, ethics, and responsibility in every interaction with clients, colleagues, and the community.',
      },
      collaboration: {
        title: 'Collaboration',
        text: 'We believe in growth through cooperation, strong partnerships, and goals built with respect.',
      },
      sustainability: {
        title: 'Sustainability',
        text: 'We make decisions that promote environmental, social, and economic health, considering both the present and the future.',
      },
    },
    final_cta: {
      title_line1: "Let's build",
      title_line2: 'something big',
      title_accent: 'together?',
      contact_button: 'Talk to us',
    },
  },

  impact: {
    hero: {
      title_line1: 'Impact that connects engineering,',
      title_line2: 'sustainability, and real results.',
      subtitle:
        'At BlueAlps, we turn vision into measurable impact through agile, accessible, and purpose-driven solutions—aligned with the United Nations Sustainable Development Goals.',
      description:
        'At BlueAlps, we believe technology can create positive impact. That’s why we align our strategies with the UN SDGs. We work towards a future that is sustainable, fair, and full of opportunity for everyone. Join us on this journey and discover how our software solutions can strengthen your business while creating long-term impact.',
      cta_primary: 'View our projects',
      cta_secondary: 'View our services',
    },

    ods: {
      title: 'Impact aligned with the Sustainable Development Goals',
      subtitle:
        'Our solutions are designed to deliver concrete results while directly contributing to the UN SDGs—with technology, responsibility, and measurable impact.',
      items: {
        ods3: {
          title: 'SDG 3 — Good Health and Well-being',
          desc: 'Projects that improve health, safety, and quality of life, supporting safe, reliable, and accessible systems.',
        },

        ods8: {
          title: 'SDG 8 — Decent Work and Economic Growth',
          desc: 'Engineering solutions that increase efficiency, reduce waste, and give teams more time for high-value work.',
        },

        ods9: {
          title: 'SDG 9 — Industry, Innovation and Infrastructure',
          desc: 'Modern, scalable, and resilient architectures to support the growth of purpose-driven SMEs—without technical compromises.',
        },

        ods12: {
          title: 'SDG 12 — Responsible Consumption and Production',
          desc: 'Reducing digital waste, optimizing resource usage, and enabling data-driven decisions for more sustainable technology.',
        },

        ods13: {
          title: 'SDG 13 — Climate Action',
          desc: 'Projects focused on energy efficiency, monitoring, and reducing the CO2 footprint of digital solutions.',
        },
      },
    },

    how: {
      title: 'Impact made by people—and proven in practice.',
      subtitle:
        'No “marketing impact.” Every delivery comes from real project work—with efficiency, responsibility, and accessibility.',
      badge: 'Team in action 🚀',
    },

    steps: {
      step1: {
        title: 'From real problems to a working plan',
        desc: 'We assess context, define metrics, and prioritize actions with the highest impact.',
      },

      step2: {
        title: 'Lean delivery with high quality',
        desc: 'Performance, Developer Experience, and maintainability stay at the center—scalable and sustainable.',
      },

      step3: {
        title: 'Measurement over gut feeling',
        desc: 'We monitor KPIs and validate outcomes: cost, energy, reliability, accessibility, and user experience.',
      },

      step4: {
        title: 'Continuous partnership',
        desc: 'Sustainable impact comes from long-term collaboration and continuous improvement.',
      },
    },

    teamImpact: {
      title: 'Our team’s projects with real impact',
      subtitle:
        'Each card shows what was delivered, by whom—and the impact it created.',
    },

    projects: {
      impactLabel: 'Impact created',

      items: {
        p1: {
          author: 'Maria — Frontend & QA',
          role: 'Platform & Data UX (Mama Ativa)',
          title:
            'Mama Ativa: More donations through gamification + data-driven shortage forecasts',
          summary:
            'A platform to encourage breast milk donations with gamified incentives and analytics, helping donors see where demand is rising—and teams detect shortages early.',
          impact:
            'Result: better donation distribution, faster response to shortage risk, and greater transparency for donors and management.',
          ods: 'SDG 3 — SDG 9 — SDG 12',
          tags: 'Data Analytics — Gamification — HealthTech',
        },

        p2: {
          author: 'Maria & Matheus — App & Backend',
          role: 'Safety App (Alerta Rosa)',
          title: 'Alerta Rosa: Faster access to help and safer everyday routes',
          summary:
            'A safety app with SOS alerts, quick emergency contacts, and location/service information to make support instantly reachable in critical situations.',
          impact:
            'Result: shorter response times, clear emergency action paths, and greater everyday safety—especially in high-stress moments.',
          ods: 'SDG 5 — SDG 11 — SDG 16',
          tags: 'Women Safety — SOS — Mobile App',
        },
      },
    },

    cta: {
      title: 'Ready to build sustainable projects with measurable impact?',
      desc: 'Tell us about your challenge. We deliver with excellence and prove impact with data.',
      button: 'Talk to BlueAlps',
    },
  },

  services: {
    hero_title: 'Specialists in digital engineering with real impact',
    hero_subtitle:
      'We provide on-demand specialists to strengthen your organization—from technical SEO and PIM to WordPress and cybersecurity, with a focus on measurable outcomes.',
    direct_contact_note: 'We contact you directly from Brazil.',
    currency_selector_kicker: 'Pricing currency',
    currency_selector_title: 'Choose the currency shown in the prices',
    currency_selector_subtitle:
      'The cards and commercial terms table below are recalculated automatically based on the selected currency.',
    currency_selector_aria_label: 'Select pricing currency',
    currency_selector_options: {
      CHF: 'Swiss franc',
      USD: 'US dollar',
      EUR: 'Euro',
    },
    cta_request_specialist: 'Request specialists',
    view_details: 'View details',
    back_to_services: 'Back to services',
    detail_what_is_title: 'What is this service?',
    detail_deliverables_title: 'What is included',
    detail_value_title: 'Price',
    detail_discount_note:
      'Higher-cost projects may receive progressive discounts based on scope, duration, and contracted volume.',
    detail_schedule_cta: 'Schedule',
    detail_booking_strip_label: 'Free meeting',
    detail_booking_strip_title: 'Talk to BlueAlps before you decide.',
    detail_booking_strip_text:
      'In a short call, we align on your goal, answer questions, and show the simplest path to get started.',
    detail_booking_reasons_label: 'Next step',
    detail_booking_reasons_title: 'Why schedule a conversation now?',
    detail_booking_reasons_text:
      'The meeting turns interest into a clear plan with priorities, scope, and concrete next steps.',
    detail_booking_reason_1_title: 'Quick diagnosis',
    detail_booking_reason_1_text:
      'We understand the current context and identify where this service can create impact fastest.',
    detail_booking_reason_2_title: 'Clear scope',
    detail_booking_reason_2_text:
      'You leave with more clarity on deliverables, required effort, and the best engagement format.',
    detail_booking_reason_3_title: 'No commitment',
    detail_booking_reason_3_text:
      'The conversation is free and helps both sides evaluate whether moving forward with BlueAlps makes sense.',
    detail_final_cta_label: 'Ready to move forward?',
    detail_final_cta_title:
      'Schedule a meeting and bring your project to life.',
    detail_final_cta_text:
      'Choose a time in Microsoft Bookings and talk directly with our team about this service.',
    detail_ideal_client_title: 'Who is this service for?',

    plan_price_from: 'Per hour',
    plan_recommended_fast_impact: 'Fast impact',
    plan_recommended_specialty: 'Our specialty',

    plan_ecommerce_title: 'E-commerce',
    plan_ecommerce_desc:
      'Creation, rebuilding, and evolution of online stores with clear catalogs, reliable checkout, integrations, and a foundation prepared to grow.',

    plan_ux_title: 'Layout & UX Optimization',
    plan_ux_desc:
      'Improve visual hierarchy, usability, and conversion with focused layout refinements and UX guidance.',

    plan_tech_seo_title: 'Technical SEO Specialists',
    plan_tech_seo_desc:
      'Improve crawling, speed, and indexing with technical SEO that translates into measurable growth.',

    plan_analytics_schema_title: 'Google Analytics & Schema',
    plan_analytics_schema_desc:
      'Measurement, events, conversions, and structured data setup to understand results and improve search presence.',

    plan_pim_title: 'PIM Specialists (Open Source)',
    plan_pim_note: ' Product Information Management',
    plan_pim_desc:
      'Build or integrate an open-source PIM to centralize product data and improve data quality.',

    plan_wordpress_title: 'WordPress Specialists',
    plan_wordpress_desc:
      'Reliable WordPress development with performance, security, and maintainability from day one.',

    plan_cyber_title: 'Cybersecurity Specialists',
    plan_cyber_desc:
      'Strengthen your security posture with practical hardening, monitoring, and incident readiness.',

    plan_digital_link_page_title: 'Digitale Linkseite',
    plan_digital_link_page_desc:
      'Personalized digital link pages that bring important links, social channels, contact details, and offers together in one place.',

    plan_individual_title: 'Individual',
    plan_business_title: 'Business',
    plan_business_recommended: 'Recommended',
    plan_custom_title: 'Custom (On-Demand)',
    plan_audit_title: 'Audit',

    plan_individual_desc:
      'Ideal for individuals and small businesses that need a simple and efficient online presence.',
    plan_business_desc:
      'For growing companies that need more pages, integrations, and dynamic content.',
    plan_design_note: 'Responsive design and prototyping.',
    plan_custom_desc:
      'Tailor-made, scalable projects—fully aligned with your needs.',

    plan_audit_feature_privacy: 'Privacy & compliance report',
    plan_audit_feature_performance: 'Performance & SEO analysis',
    plan_audit_feature_accessibility: 'Accessibility & usability review',
    plan_audit_feature_security: 'Security & risk checklist',
    plan_audit_desc:
      'A comprehensive audit of privacy, performance, and environmental impact—with clear, actionable recommendations.',

    ideal_clients: {
      ecommerce:
        'For companies that want to sell online with a reliable store that is easy to manage and prepared to connect products, payments, logistics, and marketing.',
      tech_seo:
        'For companies that already have a website, e-commerce store, or digital platform and need better visibility in search engines, speed, indexing, and technical quality for organic traffic.',
      analytics_schema:
        'For companies that need better measurement of visits, events, and conversions, plus structured data so search engines understand pages, products, services, and content.',
      pim: 'For companies with many products, data spread across spreadsheets, ERP, or e-commerce systems, and teams that need to organize product information with more quality and automation.',
      wordpress:
        'For businesses that use or want to use WordPress and need a faster, safer, easier-to-maintain website prepared for growth.',
      ux: 'For companies seeing low conversion, confusing navigation, outdated layouts, or users struggling to find and complete important actions.',
      digital_link_page:
        'For professionals, brands, and businesses that need a simple, polished, organized page to concentrate links, social channels, contact details, and offers.',
      cyber:
        'For organizations that handle sensitive data, internal access, online systems, or critical websites and need to reduce security risks in a practical way.',
      audit:
        'For companies that want to quickly understand the main privacy, performance, SEO, accessibility, and security issues before investing in improvements.',
    },

    detail_outcomes_title: 'Expected outcomes',
    detail_when_to_use_title: 'When to hire it',
    detail_workflow_title: 'How we work',
    detail_timeline_period: 'Step {{count}}',
    detail_timeline_keyword: 'MAIN PHASE',
    detail_faq_title: 'Frequently asked questions about this service',
    service_details: {
      ecommerce: {
        overview:
          'The E-commerce service creates, rebuilds, or evolves online stores with a focus on a well-organized catalog, reliable checkout, essential integrations, and an operation prepared to grow.',
        outcomes: [
          'A clearer, more professional online store that is easier to manage.',
          'Better structured catalog, product pages, and purchase journey.',
          'Integrations with payments, logistics, ERP, PIM, or marketing tools.',
          'A technical foundation prepared for performance, SEO, measurement, and continuous evolution.',
        ],
        when_to_use: [
          'You want to launch a new online store.',
          'Your current store is hard to maintain, slow, or unreliable.',
          'Catalog, checkout, or integrations are limiting growth.',
          'You need to connect e-commerce with product data, ERP, or campaigns.',
        ],
        workflow: [
          'We understand catalog, purchase journey, integrations, and commercial rules.',
          'We plan architecture, platform, pages, data, and checkout flows.',
          'We implement the store with a focus on performance, usability, and simple management.',
          'We test purchase, payments, integrations, and deliver documentation for operations.',
        ],
        workflow_titles: ['Understand', 'Plan', 'Build', 'Deliver'],
        faq: [
          {
            question: 'Do you create stores from scratch?',
            answer:
              'Yes. We can create a new store or rebuild an existing one, defining platform, structure, catalog, checkout, and integrations according to scope.',
          },
          {
            question: 'Do you integrate with ERP, PIM, or payment providers?',
            answer:
              'Yes. We evaluate the required integrations and prioritize the connections that reduce manual work and improve operations.',
          },
          {
            question: 'When does it make sense to use PIM with e-commerce?',
            answer:
              'It makes sense when the store has many products, variations, languages, sales channels, or recurring product data rework. The PIM centralizes information and feeds the e-commerce with more consistent data.',
          },
          {
            question: 'Will the store be prepared for SEO and analytics?',
            answer:
              'Yes. We organize structure, pages, events, and technical foundations to support measurement, indexing, and evolution.',
          },
          {
            question: 'Which platform do you use for e-commerce?',
            answer:
              'It depends on the project. We evaluate catalog, integrations, budget, team autonomy, and growth plans before recommending the most suitable platform.',
          },
          {
            question: 'Do you add products?',
            answer:
              'We can support the initial product setup, category structure, attributes, variations, and images. For larger catalogs, we recommend organizing data with a PIM or well-structured spreadsheets.',
          },
          {
            question: 'Do you migrate an existing store?',
            answer:
              'Yes. First, we analyze the current platform, products, URLs, integrations, orders, customers, and SEO risks to define a migration with the lowest possible impact.',
          },
          {
            question: 'Can the store include payments and shipping?',
            answer:
              'Yes. We can integrate payment methods, shipping rules, delivery calculation, and logistics solutions depending on the technical availability of the platform and providers.',
          },
          {
            question: 'Can you improve a store that is already live?',
            answer:
              'Yes. We can optimize performance, product pages, checkout, analytics events, technical SEO, integrations, and friction points in the purchase journey.',
          },
          {
            question: 'Can I manage the store after delivery?',
            answer:
              'Yes. We structure the store to make daily management easier and provide guidance or documentation so your team can update products, content, and basic settings.',
          },
          {
            question: 'How long does an e-commerce project take?',
            answer:
              'The timeline depends on scope, number of pages, product volume, integrations, and migration needs. After the initial analysis, we define a realistic phased schedule.',
          },
        ],
      },
      tech_seo: {
        overview:
          'Technical SEO improves the technical foundation of your website so search engines can crawl, understand, and index important pages more effectively. The work connects performance, structure, tracking, and prioritization into a clear growth plan.',
        outcomes: [
          'Clear visibility into technical issues that block ranking potential.',
          'Better speed, Core Web Vitals, and mobile experience.',
          'A structure that is easier for search engines and analytics tools to read.',
          'An action plan prioritized by impact, effort, and business risk.',
        ],
        when_to_use: [
          'Organic traffic is low, unstable, or difficult to explain.',
          'Important pages are not performing well in search engines.',
          'The site feels slow, heavy, or weak on mobile.',
        ],
        workflow: [
          'Technical diagnosis of crawling, indexing, speed, and structure.',
          'Prioritization of issues with the highest visibility and conversion impact.',
          'Implementation support or technical guidance for your team.',
          'Monthly or biweekly report tracking key metrics and the evolution of results.',
        ],
        workflow_titles: [
          'Technical audit',
          'Prioritization',
          'Implementation',
          'Follow-up',
        ],
        faq: [
          {
            question: 'How long does technical SEO take to show results?',
            answer:
              'Performance and indexing fixes can show signals within a few weeks, but ranking gains depend on domain history, competition, content, and Google crawl frequency.',
          },
          {
            question: 'Do you implement fixes or only deliver a report?',
            answer:
              'Both are possible. We can deliver a technical plan for your team or support implementation, validation, and follow-up directly.',
          },
          {
            question: 'Does technical SEO replace content or ads?',
            answer:
              'No. It strengthens the foundation so content and campaigns perform better. Often, fixing technical issues increases the return of existing content.',
          },
        ],
      },
      analytics_schema: {
        overview:
          'Google Analytics & Schema organizes website measurement and implements structured data so your team can track results more clearly and search engines can better understand your pages.',
        outcomes: [
          'Google Analytics configured with relevant events and conversions.',
          'Google Tag Manager organized for easier future maintenance.',
          'Google Shopping events prepared to track important commercial interactions.',
          'Schema markup applied to important pages, services, products, or content.',
          'Technical validation to reduce measurement and structured data errors.',
        ],
        when_to_use: [
          'You do not trust your current traffic and conversion data.',
          'Important events are not being measured correctly.',
          'You want to improve how search tools read your site.',
          'Campaigns and decisions need more reliable data.',
        ],
        workflow: [
          'We map goals, events, conversions, and priority pages.',
          'We configure required tags, events, parameters, and structured data.',
          'We validate collection, triggers, reports, and schema markup.',
          'We document the setup for maintenance and evolution.',
        ],
        workflow_titles: ['Mapping', 'Setup', 'Validation', 'Delivery'],
        faq: [
          {
            question: 'Do you configure GA4 and Google Tag Manager?',
            answer:
              'Yes. We can configure GA4, GTM, events, conversions, parameters, and Google Shopping events according to the website goals.',
          },
          {
            question: 'Does schema help SEO?',
            answer:
              'It helps structure information for search engines. It does not guarantee rankings, but it improves technical clarity about pages, products, services, and content.',
          },
          {
            question: 'Can you fix inconsistent analytics data?',
            answer:
              'Yes. We review the current implementation, find duplicates or gaps, and reorganize data collection.',
          },
        ],
      },
      pim: {
        overview:
          'PIM centralizes product information into a reliable source of truth across e-commerce, ERP, catalogs, marketplaces, and internal teams. It improves data quality, commercial consistency, and publication speed.',
        outcomes: [
          'Centralized product data that is easier to maintain.',
          'Fewer errors in descriptions, attributes, categories, images, and translations.',
          'More stable integrations between business systems and digital channels.',
          'Faster import, validation, and export processes.',
        ],
        when_to_use: [
          'Product data is managed in many spreadsheets or systems.',
          'Teams spend too much time correcting repeated information.',
          'You sell through multiple channels and need consistency.',
          'Catalog growth is making operations harder to control.',
        ],
        workflow: [
          'Mapping of current data, connected systems, and business rules.',
          'Modeling of attributes, categories, validations, and update flows.',
          'Integration with ERP, e-commerce, APIs, or import files.',
          'Training and documentation so your team can operate independently.',
        ],
        workflow_titles: ['Mapping', 'Modeling', 'Integration', 'Training'],
        faq: [
          {
            question: 'Do I need to replace my ERP or e-commerce to use a PIM?',
            answer:
              'Usually not. The PIM acts as a central data layer and can integrate with the ERP, e-commerce, marketplaces, or files your operation already uses.',
          },
          {
            question: 'How does PIM help an e-commerce operation?',
            answer:
              'It centralizes descriptions, attributes, images, categories, and translations so the store receives more complete and consistent data, reducing errors and accelerating publication.',
          },
          {
            question: 'Is PIM useful for small catalogs?',
            answer:
              'It can be, but the strongest return appears with many SKUs, variations, languages, sales channels, or recurring product data rework.',
          },
          {
            question: 'Does the team receive training?',
            answer:
              'Yes. Delivery includes documentation and guidance so product, marketing, and technology teams can maintain data quality.',
          },
        ],
      },
      wordpress: {
        overview:
          'WordPress service covers creation, rebuilds, optimization, and technical maintenance for WordPress websites. The focus is a stable, secure, fast, and easy-to-manage foundation without unnecessary plugin complexity.',
        outcomes: [
          'More speed: shorter loading times and a better experience for people visiting the site.',
          'Less complexity: plugins, theme, and settings reviewed to avoid unnecessary dependencies.',
          'More security: a protected installation with a more reliable routine for updates and backups.',
          'More autonomy: an organized structure so your team can manage content without depending on a developer for every change.',
          'More predictability: a WordPress setup prepared for maintenance, evolution, and new features.',
        ],
        when_to_use: [
          'A new website: you need to create or rebuild an institutional website with a professional structure that is easy to manage and prepared to grow.',
          'A slow website: your WordPress takes too long to load, has performance issues, or has accumulated plugins, scripts, and settings over time.',
          'A website that needs care: you want to keep your site updated, secure, and stable without turning your team into WordPress specialists.',
        ],
        workflow: [
          'We analyze the site, its goals, content, current structure, and main problems.',
          'We define the architecture, tools, and solutions required for the project.',
          'We implement the site with a focus on performance, security, usability, and ease of maintenance.',
          'We publish, test, and document the project so your team knows how to manage the site.',
        ],
        workflow_titles: ['Understand', 'Plan', 'Build', 'Deliver'],
        faq: [
          {
            question: 'Do you work with existing WordPress sites?',
            answer:
              'Yes. We can take over sites built by other teams or agencies for maintenance, optimization, or rebuilding. Before changing anything, we run a technical analysis to identify issues with structure, plugins, performance, and security.',
          },
          {
            question: 'Can plugins be reduced without losing functionality?',
            answer:
              'In most cases, yes. We analyze which plugins are truly necessary, which can be replaced by native resources or simpler solutions, and which are causing conflicts or overhead.',
          },
          {
            question: 'Is WordPress secure?',
            answer:
              'Yes, as long as it is correctly configured and maintained. We work with updates, permission control, hardening, backups, and other measures to reduce the main risks of a WordPress installation.',
          },
          {
            question: 'My site is slow. Can you find the problem?',
            answer:
              'Yes. We analyze the installation, hosting, database, images, cache, plugins, and scripts to identify the main bottlenecks and prioritize the optimizations that truly make a difference.',
          },
          {
            question: 'Do you work with Elementor?',
            answer:
              'Yes. We can work with existing Elementor sites and also develop new projects using the builder when it makes sense for the project.',
          },
          {
            question: 'Do you provide maintenance after delivery?',
            answer:
              'Yes. The project can be handed over for your team to manage or continue with us through a maintenance plan.',
          },
          {
            question: 'Can you rebuild a site without starting from scratch?',
            answer:
              'Yes. First, we evaluate what can be reused and what is causing problems. In many cases, rebuilding only the necessary structure is more efficient than replacing the entire site.',
          },
          {
            question: 'How much does a WordPress project cost?',
            answer:
              'The price depends on scope, number of pages, integrations, level of customization, and the current state of the site. For larger projects, we do an initial analysis and present a proposal based on the defined scope.',
          },
          {
            question: 'Why use WordPress?',
            answer:
              'WordPress offers a combination of flexibility, autonomy, and room for evolution that works well for many institutional websites and digital projects.\n\nBut we do not recommend WordPress simply because it is WordPress. We evaluate the project context and choose the solution that makes sense for the business.',
          },
        ],
      },
      ux: {
        overview:
          'Modernization of the website interface with a focus on usability, accessibility, and user experience. It includes improved navigation, content organization, responsiveness, visual hierarchy, element standardization, and the creation of more intuitive, pleasant interfaces aligned with the brand identity.',
        outcomes: [
          'Clearer pages that are easier to scan and understand.',
          'Buttons, forms, and journeys with less user friction.',
          'Better visual consistency across desktop, tablet, and mobile.',
          'Practical recommendations to improve conversion and trust.',
        ],
        when_to_use: [
          'Users leave pages before completing important actions.',
          'The layout feels outdated, confusing, or inconsistent.',
          'The mobile navigation is uncomfortable.',
          'You want conversion gains without rebuilding the entire product.',
        ],
        workflow: [
          'Analysis of the current experience, hierarchy, flows, and friction points.',
          'Prioritization of improvements focused on clarity and conversion.',
          'Prototyping or implementation of layout refinements.',
          'Responsive review and recommendations for the next cycles.',
        ],
        workflow_titles: [
          'UX analysis',
          'Prioritization',
          'Prototype',
          'Review',
        ],
        faq: [
          {
            question: 'Do I need to rebuild the whole site to improve UX?',
            answer:
              'Not always. Many improvements come from hierarchy, spacing, navigation, buttons, forms, and responsive adjustments without rebuilding everything.',
          },
          {
            question: 'Do you deliver a prototype or implement changes?',
            answer:
              'It depends on scope. We can deliver prototypes, visual recommendations, or implement adjustments directly in the product.',
          },
          {
            question: 'Does UX help conversion?',
            answer:
              'Yes. A clearer interface reduces friction, increases trust, and makes actions like contact, purchase, signup, or quote requests easier.',
          },
          {
            question: 'Can the reading experience be improved?',
            answer:
              'Yes. We can adjust copy, font sizes, spacing, and organization to make reading more comfortable.',
          },
          {
            question: 'Can you improve forms?',
            answer:
              'Yes. We can simplify fields, improve instructions, and make forms easier to complete.',
          },
          {
            question: 'Does dark theme work automatically?',
            answer:
              'It can work manually or follow the preference configured on the user device.',
          },
          {
            question: 'Can optimization improve the mobile experience?',
            answer:
              'Yes. We can review sizes, menus, buttons, and content layout for smaller screens.',
          },
          {
            question: 'Can the current style be kept and only modernized?',
            answer:
              'Yes. A completely new identity is not required to improve the layout.',
          },
          {
            question: 'Can the website copy also become clearer?',
            answer:
              'Yes. We can improve navigation text, titles, calls to action, and content to make the site easier to understand.',
          },
          {
            question: 'Does optimization change the brand identity?',
            answer:
              'Not necessarily. We can preserve colors, logo, and brand characteristics while modernizing the interface.',
          },
          {
            question: 'Can overloaded pages be improved?',
            answer:
              'Yes. We can reorganize elements and reduce information overload to make the experience lighter.',
          },
        ],
      },
      digital_link_page: {
        overview:
          'We create personalized digital link pages that clearly bring together important links, social media channels, contact details, and offers in one place.',
        outcomes: [
          'A link page with visual identity aligned with the brand.',
          'Links, social channels, contact details, and offers organized clearly.',
          'A fast, responsive experience for mobile, tablet, and desktop.',
          'A simple structure to maintain and prepare for campaigns.',
        ],
        when_to_use: [
          'You need a more professional alternative to a link-in-bio page.',
          'Important links are spread across too many channels.',
          'You want to highlight contacts, social channels, offers, or campaigns.',
          'Your team needs to update links with more organization.',
        ],
        workflow: [
          'We map links, channels, offers, and communication priorities.',
          'We define page structure, visual hierarchy, and identity.',
          'We build a lightweight, responsive page that is easy to access.',
          'We publish and guide your team on future updates.',
        ],
        workflow_titles: ['Mapping', 'Structure', 'Build', 'Publishing'],
        faq: [
          {
            question: 'Does this replace a full website?',
            answer:
              'Not necessarily. The link page works as a quick entry point for channels, contacts, and offers, but it can complement a full website.',
          },
          {
            question: 'Can the page follow the brand identity?',
            answer:
              'Yes. We can adapt colors, typography, copy, and organization so it stays aligned with the brand.',
          },
          {
            question: 'Can clicks be measured?',
            answer:
              'Yes. We can configure measurement to track clicks and understand which links generate more interaction.',
          },
        ],
      },
      cyber: {
        overview:
          'Cybersecurity identifies practical risks in websites and systems. The goal is to strengthen protection with applicable measures, clear priorities, and follow-up on critical points.',
        outcomes: [
          'A clear view of the most important technical and operational risks.',
          'Better control of access, authentication, permissions, and data exposure.',
          'A report with identified vulnerabilities, correction priorities, and improvement recommendations.',
          'Reduced common vulnerabilities in websites, APIs, servers, and dependencies.',
        ],
        when_to_use: [
          'Your website or system handles sensitive customer data.',
          'There are many users, internal accesses, or permissions without review.',
          'You need to prepare for audits, growth, or external integrations.',
          'Updates, backups, and monitoring are not yet structured.',
        ],
        workflow: [
          'Mapping of assets, access, integrations, and risk surfaces.',
          'Review of settings, dependencies, permissions, and critical practices.',
          'Correction plan with severity, effort, and impact.',
          'Follow-up on improvements and continuous monitoring recommendations.',
        ],
        workflow_titles: [
          'Discovery',
          'Assessment',
          'Correction',
          'Monitoring',
        ],
        faq: [
          {
            question: 'Does this include penetration testing?',
            answer:
              'The standard scope focuses on practical assessment, hardening, and risk reduction. Deeper offensive testing can be defined separately based on need and authorization.',
          },
          {
            question: 'Do you fix the issues found?',
            answer:
              'We can fix them directly or work with your team, prioritizing vulnerabilities by severity, exposure, and implementation effort.',
          },
          {
            question: 'Is this service only for large companies?',
            answer:
              'No. Small and medium-sized companies also need to protect access, data, backups, dependencies, and websites, especially when they depend on digital channels.',
          },
        ],
      },
      audit: {
        overview:
          'The Audit provides a fast, structured view of privacy, performance, SEO, accessibility, usability, and security. It is ideal for understanding the main problems before starting a larger project or prioritizing improvements.',
        outcomes: [
          'Objective diagnosis of the website main strengths and weaknesses.',
          'Prioritized improvement list with expected impact explained.',
          'Technical evidence to support investment decisions.',
          'Actionable recommendations for short- and medium-term next steps.',
        ],
        when_to_use: [
          'You want to know where to start before hiring development work.',
          'The site exists, but commercial or technical results are unclear.',
          'There are doubts about privacy, SEO, accessibility, or security.',
          'You need to present priorities to stakeholders or leadership.',
        ],
        workflow: [
          'Collection of context, goals, and URLs or systems to evaluate.',
          'Technical analysis with tools, manual review, and business criteria.',
          'Organization of findings by severity, impact, and correction effort.',
          'Report delivery with clear recommendations and an alignment call.',
        ],
        workflow_titles: ['Context', 'Analysis', 'Prioritization', 'Report'],
        faq: [
          {
            question: 'Is the audit useful before a larger project?',
            answer:
              'Yes. It helps identify real priorities before investing in redesign, development, SEO, security, or performance improvements.',
          },
          {
            question: 'Is the report too technical?',
            answer:
              'No. We organize findings with clear explanations, business impact, and next steps, keeping technical details where they help implementation.',
          },
          {
            question: 'Can you execute improvements after the audit?',
            answer:
              'Yes. The audit can become an execution plan with prioritization, budget, and follow-up on corrections.',
          },
        ],
      },
    },

    features: {
      ux_modern_layout: 'Modern layout: a more current and professional look.',
      ux_better_navigation:
        'Improved navigation: easier access to information.',
      ux_usability: 'More usability: a simple and practical experience.',
      ux_accessibility: 'Accessibility: better reading and interaction.',
      ux_responsive_design: 'Responsive design: adaptation for all devices.',
      ux_intuitive_interface:
        'Intuitive interface: clearer actions and information.',
      ux_light_dark_theme:
        'Light and dark theme: more comfort and personalization.',

      link_page_links: 'Important links organized in one place',
      link_page_socials: 'Social media channels highlighted clearly',
      link_page_contact: 'Contact details accessible and easy to use',
      link_page_offers: 'Offers, campaigns, or calls to action highlighted',
      link_page_responsive: 'Fast, responsive, and personalized page',

      up_to_pages: 'Up to {{count}} pages',
      unlimited_pages: 'Unlimited pages',
      text_images_tables: 'Text, images, and tables',
      nav_footer: 'Navigation and footer',
      cookie_banner: 'Cookie consent banner',
      professional_email: 'Professional email',
      third_party_api: 'Third-party API integration',
      cms_database: 'CMS / database',
      ecommerce: 'E-commerce',
      previous_features: 'All features from previous plans',
      full_ecommerce: 'Full e-commerce setup',
      on_demand: 'On-demand features',

      deliverable_seo_audit:
        'Technical SEO analysis with a clear action plan to increase visibility',
      deliverable_cw_vitals: 'Speed and user experience optimization',
      deliverable_accessibility: 'Ensure accessibility compliance',
      deliverable_indexing: 'Improved discoverability and indexing',
      delivery_weekly_report: 'Monthly performance report and next steps',

      ga4_setup: 'Google Analytics 4 setup',
      gtm_events: 'Events, conversions, and Google Tag Manager',
      google_shopping_events: 'Google Shopping events as an option',
      schema_markup: 'Schema markup for pages, products, and services',
      analytics_validation: 'Technical validation of data and tracking',

      pim_integration:
        'PIM integration with e-commerce / ERP (Oracle, Abacus, Bling)',
      pim_data_model: 'Data model and attribute strategy',
      pim_import_export: 'Import/export pipelines and automation',
      pim_quality_rules: 'Data quality and validation rules',

      delivery_hand_over:
        'Clear documentation and training so your team can own it',

      ecommerce_store_setup: 'Online store creation or rebuilding',
      ecommerce_catalog_checkout: 'Structured catalog, product, and checkout',
      ecommerce_integrations: 'Payment, logistics, ERP, or PIM integrations',
      ecommerce_performance_tracking:
        'Performance, SEO, and measurement prepared to grow',

      wp_build_or_rebuild: 'Creation and rebuilding',
      wp_performance: 'Performance and speed',
      wp_security_hardening: 'Security and stability',
      wp_plugins_quality: 'Plugins, theme, and architecture',
      delivery_monthly_maintenance: 'Ongoing maintenance',
      wp_technical_seo: 'Technical SEO',

      cyber_risk_assessment: 'Risk assessment and threat analysis',
      cyber_website_security: 'Website security analysis',
      cyber_api_security: 'API security',
      cyber_code_security: 'Code security analysis',
      cyber_monitoring: 'Monitoring and alerts for critical risks',
      delivery_security_report: 'Security report with priorities and timeline',
    },

    cta_get_started: 'Get started',
    cta_request_proposal: 'Request a proposal',
    whatsapp_message:
      'Hi! I want to hire the "{{service}}" service for {{price}}, as shown on the website.',
    long_term_note:
      'Special terms available for long-term projects and contracts.',
    long_term_cta: 'Discuss your project now!',
    long_term_whatsapp_message:
      'Hi! I would like to discuss a project and learn about the special terms available for long-term projects and contracts.',
    price_on_request: 'price on request',
    commercial_table_title: 'Commercial terms',
    commercial_table: {
      billing: 'Billing',
      hourly_rate: 'Hourly rate',
      commitment: 'Commitment',
      rows: {
        row_0: {
          billing: '0',
          hourly_rate: '50 CHF',
          commitment: 'No commitment',
        },
        row_1: {
          billing: "15'000",
          hourly_rate: '45 CHF',
          commitment: '4 days per month. Minimum duration of 3 months.',
        },
        row_2: {
          billing: "30'000",
          hourly_rate: '40 CHF',
          commitment: '8 days per month. Minimum duration of 3 months.',
        },
        row_3: {
          billing: "45'000",
          hourly_rate: '35 CHF',
          commitment: '14 days per month. Minimum duration of 6 months.',
        },
        row_4: {
          billing: "60'000",
          hourly_rate: '30 CHF',
          commitment: '21 days per month. Minimum duration of 6 months.',
        },
      },
    },

    extras_title: 'Extras & Maintenance',

    extras: {
      cookie_banner: {
        title: 'Cookie Banner',
        desc: 'Stay privacy-compliant and build trust with your visitors.',
      },

      professional_email: {
        title: 'Professional Email',
        desc: 'Email setup with your brand for a more professional presence.',
      },

      audits: {
        title: 'Audits',
        desc: 'Comprehensive audits of privacy, performance (SEO), and environmental impact (CO2 footprint).',
      },

      maintenance: {
        title: 'Maintenance',
        desc: 'Keep your website secure and up to date.',
        tier_individual: '(Individual)',
        tier_business: '(Business)',
        tier_custom: '(Custom)',
      },
    },

    faq_title_prefix: 'Frequently',
    faq_title_accent: 'asked questions',

    faq: {
      q1: 'What is a PIM and what is it for?',
      a1: 'A PIM (Product Information Management) is a system that centralizes, organizes, validates, and distributes product information across all sales and communication channels—such as e-commerce, ERP, marketplaces, catalogs, and APIs. This keeps data consistent, improves data quality, and accelerates product publishing.',
      q2: 'Can my team use the PIM easily?',
      a2: 'Yes. The PIM is configured so product, marketing, and tech teams can use it smoothly in day-to-day work. The project also includes clear documentation, training, and governance best practices—so your team can operate autonomously and efficiently.',
      q3: 'What layout & UX improvements do you actually make in practice?',
      a3: 'We improve layout & UX in a very practical way: we make the site truly responsive (mobile, tablet, and desktop work properly), refine hierarchy, spacing, typography, and contrast so the UI looks cleaner, higher-quality, and more professional. We also optimize navigation and buttons for a more intuitive, smoother experience—less friction, more clarity—plus accessibility improvements so it works well for everyone.',
      q4: 'What is SEO and why is it important for my website?',
      a4: 'SEO (Search Engine Optimization) is a set of practices that improves your website’s visibility in search results (e.g., Google). Good SEO increases reach, brings qualified traffic, and creates more organic business opportunities—without relying only on paid ads.',
      q6: 'What actions can I take to improve my Google ranking?',
      a6: 'Key actions include optimizing titles and descriptions, using relevant keywords, creating high-quality content, improving site speed, ensuring a strong mobile experience, and structuring your site technically so search engines can understand it better.',
      q7: 'What is cybersecurity and how does it protect my website or system?',
      a7: 'Cybersecurity includes practices, technologies, and processes that protect systems, networks, and data from digital attacks, unauthorized access, and data leaks—ensuring integrity, confidentiality, and availability.',
      q8: 'What are the most important measures to protect my data and users?',
      a8: 'Some of the most important measures include SSL certificates, strong passwords, two-factor authentication, regular updates of systems and dependencies, frequent backups, access control, and continuous monitoring for threats.',
      q9: 'What concrete outcomes can an audit bring to my company?',
      a9: 'An audit delivers direct business outcomes: faster speed and better user experience (especially on mobile), reduced navigation friction, higher conversions (purchase, registration, proposal requests); stronger technical SEO to increase organic traffic; identification and remediation of privacy and compliance gaps to reduce risk and build trust; a focused security checklist to address vulnerabilities; improved accessibility and usability; and optimizations that reduce page weight and resource usage—lowering costs and environmental impact.',
      q10: 'What are WordPress projects?',
      a10: 'WordPress projects are engagements where a website (or part of it) is built, customized, or optimized using WordPress—a platform for creating and managing content. In practice, this includes corporate sites, blogs, landing pages, online stores with WooCommerce, layout and responsiveness adjustments, plugin configuration (SEO, performance, forms, security), integrations (CRM, analytics, payments), and ongoing maintenance, updates, and bug fixes to keep the site fast, secure, and easy to use.',
    },
  },

  customers: {
    title: 'Our customers',
  },

  marquee: {
    ariaLabel: 'Highlights',
    line1: 'CUSTOM DEVELOPMENT — API INTEGRATIONS — CMS — E-COMMERCE',
    line2: 'TECHNICAL SEO — PERFORMANCE — ACCESSIBILITY — WEB VITALS — AUDITS',
  },

  pagenotfound: {
    tagline: 'Off the mountain trail—follow the lake’s blue back home.',
    backHome: 'Back to home',
    notFound: 'Page not found',
  },

  welcome_message: 'Welcome to my portfolio',

  instagramLinks: {
    ariaLabel: 'BlueAlps social links',
    shareLabel: 'Share',
    bio: [
      'Swiss technology, Brazilian talent.',
      'We develop innovative digital solutions',
      'for companies that want to grow.',
    ],
    cards: {
      instagram: {
        title: 'Instagram',
        subtitle: 'Follow our updates, projects and insights.',
        cta: 'View profile',
      },
      website: {
        title: 'BlueAlps',
        subtitle: 'Explore our digital solutions and work.',
        cta: 'Visit website',
      },
      whatsapp: {
        title: 'WhatsApp',
        subtitle: 'Talk directly with our team.',
        cta: 'Send message',
      },
      booking: {
        title: 'Microsoft Bookings',
        subtitle: 'Schedule a free meeting with our team.',
        cta: 'Book now',
      },
    },
    footer: [
      { label: 'Cookie Preferences', href: '/cookie-policy' },
      { label: 'Report', href: '/contact' },
      { label: 'Privacy', href: '/privacy-policy' },
      { label: 'About this account', href: '/imprint' },
      { label: 'More from BlueAlps', href: '/' },
    ],
  },

  footer: {
    extra_links: 'Additional links',
    home: 'Home',
    about: 'About us',
    services: 'Services',
    project: 'Projects',
    impact: 'Impact',
    contact_button: 'Contact',
    imprint: 'Imprint',
    privacy: 'Privacy Policy',
    cookie: 'Cookie Policy',
  },

  home: {
    hero: {
      eyebrow: 'RESPONSIBLE IT',
      title_line1: 'High-quality software,',
      title_line2: 'with reduced costs.',
    },

    cta_primary: 'Get started',
    caption: 'Software development with quality and cost efficiency.',
    more_info: 'Learn more about us',
    more_info_services: 'Discover our services now',

    services_teaser_title:
      'Find experts to accelerate your digital transformation',
    services_teaser_subtitle:
      'On-demand experts in software development, artificial intelligence, SEO, PIM, WordPress, cybersecurity, and much more.',
    services_teaser_price_note:
      'Talk to us to find the right specialist for your challenge.',
    services_teaser_cta: 'Explore our services',
    services_teaser_custom_quote: 'Request a custom quote',

    purposetitle_part1: 'Technology that connects',
    purposetitle_part2: 'innovation and impact',

    purpose:
      'BlueAlps is a Swiss-Brazilian technology company specializing in custom software development, artificial intelligence, automation, and digital transformation for businesses.\n\nWe combine Brazilian creativity with Swiss precision to deliver scalable, secure, and high-quality technology solutions that help organizations innovate, optimize processes, and accelerate growth. Our work is driven by technical excellence and a commitment to creating positive impact, always guided by our values of equity, inclusion, integrity, collaboration, and sustainability.',
    purpose_values_label: 'BlueAlps values',
    purpose_values: {
      equity: 'Equity',
      inclusion: 'Inclusion',
      integrity: 'Integrity',
      collaboration: 'Collaboration',
      sustainability: 'Sustainability',
    },
    company_cta: 'Learn more about our company',

    vision_title: 'Vision',
    vision_text:
      'Our vision is to reduce social inequalities and foster a fairer, more inclusive, diverse, and sustainable world by combining the strengths of two complementary cultures—Brazil and Switzerland. We inspire and enable positive change through socially committed engineering that blends technical innovation with social responsibility.',

    mission_title: 'Mission',
    mission_text:
      'Our mission is to support small and medium-sized businesses with accessible, high-performance software solutions aligned with the UN Sustainable Development Goals (SDGs). We combine technical expertise with ways of working that promote equity, equal opportunity, diversity, and sustainability. Beyond our services, we live the values we stand for—creating a safe, inclusive, and ethically grounded environment for our team and our clients.',

    core_values: 'Core values',
    core_values_text:
      'An organization’s values are the foundation of its identity and guide every decision. They shape the company culture and the impact we create.',

    core_values_list_Equity: 'Equity',
    core_values_list_Equity_text:
      'Fair opportunities and outcomes for everyone—through equity in partnerships, processes, and the workplace.',

    core_values_list_InclusionDiversity: 'Inclusion & Diversity',
    core_values_list_InclusionDiversity_text:
      'Creating welcoming environments that value different voices, backgrounds, and experiences.',

    core_values_list_Integrity: 'Integrity',
    core_values_list_Integrity_text:
      'Acting transparently and ethically in all interactions—with clients, colleagues, and the community.',

    core_values_list_Sustainability: 'Sustainability',
    core_values_list_Sustainability_text:
      'Making decisions that promote environmental, social, and economic well-being—today and in the future.',

    core_values_list_Collaboration: 'Collaboration',
    core_values_list_Collaboration_text:
      'Growing through joint action and strong partnerships based on respect and shared goals.',

    two_nations1: 'Ideas without borders:',
    two_nations2: 'uniting two nations',

    switzerland: 'Switzerland',
    brazil: 'Brazil',

    linkedin_link_label: 'View LinkedIn - Profile of {{name}}',
    linkedin_link_text: 'View LinkedIn',

    our_mission: 'Our mission',
    our_mission_text:
      'We believe software should be a tool for social transformation. At BlueAlps, we don’t separate technical excellence from sustainability.',

    our_essence: 'Our essence',
    our_essence_text:
      'We live our values. We act responsibly. We lead with empathy. Because a sustainable future is not a distant idea—it is a collective decision we make every day.',

    our_commitment: 'Our commitment',
    our_commitment_text:
      'We work with purpose. We act responsibly. We lead with empathy. Because a sustainable future is not a distant idea—it is a collective decision we make every day.',

    value_proposition: 'Value proposition',
    value_proposition_text:
      'We deliver cutting-edge software with soul, create measurable business outcomes, and generate positive community impact—efficiency paired with social responsibility.',

    develop_conscious1:
      'We build conscious digital solutions, tailored for those who',
    develop_conscious2: 'believe in technology with impact',

    develop_conscious_text:
      'At BlueAlps, we reduce social inequalities through smart, accessible engineering. Inclusion, dignity, and collaboration guide everything we do—together with partners who share our values.',

    commitment: 'Commitment',
    commitment_text:
      'BlueAlps is more than a consultancy—it is a commitment to meaningful transformation. Our work is purpose-driven, values-based, and built in partnership with those who, like us, believe in a more balanced and sustainable world.',

    software_development: 'Software development',
    software_development_text:
      'We believe engineering can—and must—be a tool for social transformation. At BlueAlps, we don’t separate technical excellence from human impact. We combine Swiss engineering precision with Brazilian creativity and resilience to build solutions that are efficient and fair. We work with purpose, act responsibly, and lead with empathy. Because a sustainable future is not a distant idea—it is a collective decision we make daily in design, processes, and relationships.',

    ecommerce: 'E-commerce',
    ecommerce_text:
      'BlueAlps supports purpose-driven businesses with high-quality, accessible, and impact-oriented engineering solutions aligned with global sustainability goals. We connect technical precision, inclusive practices, and deep business understanding to build e-commerce platforms that drive growth and social change.',

    team_text1: 'We unite global talent to develop high-performance software',
    team_text2: 'and generate more value for your business',

    customers: 'Who Trusts Blue Alps',
    customers_title_prefix: 'Who',
    customers_title_accent: 'Trusts',
    customers_title_suffix: 'Blue Alps',
  },

  projects: {
    title: 'Projects with impact',
    subtitle:
      'Innovative solutions shaping a more efficient and sustainable future.',
    view_details: 'View details',

    items: [
      {
        id: 'divina',
        category: 'E-commerce',
        title: 'Divina E-Commerce',
        description:
          'A Swiss e-commerce platform for bedding, bath, and table linens. A multilingual, responsive website focused on usability, product variants, a tailored checkout, and integrations with local systems.',
      },

      {
        id: 'farol',
        category: 'SEO',
        title: 'Farol SEO',
        description:
          'An automated SEO audit platform that analyzes entire websites and delivers clear, actionable reports on SEO, performance, and accessibility—ideal for improving your digital presence.',
      },

      {
        id: 'gallery',
        category: 'Admin system',
        title: 'Gallery',
        description:
          'Galerie Gleis 4 developed a communication app to send mass emails to customers. A guest list from a spreadsheet enables fast, efficient invitation campaigns.',
      },

      {
        id: 'muller',
        category: 'WordPress',
        title: 'Müller Project',
        description:
          'Migration from a proprietary CMS with no significant maintenance for over 10 years to WordPress, including a technical upgrade, Swiss legal compliance, a familiar admin interface, structured content, and an integrated PDF user manual. Client feedback: I appreciated that, when it came time to launch, you responded very quickly to questions, even after the site was already online.',
      },

      {
        id: '1001',
        category: 'Maintenance',
        title: '1001 Project',
        description:
          'Advanced internship project focused on corrective and evolutionary maintenance, including an update to the MongoDB integration to replace an obsolete version and ensure greater compatibility, security, and operational continuity.',
      },

      {
        id: 'HausHeld',
        category: 'SEO and AI',
        title: 'HausHeld',
        description:
          'Digital presence optimization with SEO strategies to improve performance, search visibility, and organic traffic, plus an Artificial Intelligence solution designed to make navigation more efficient and intuitive.',
      },
    ],
  },

  contact: {
    title: 'Contact',
    subtitle: 'Talk to BlueAlps directly on WhatsApp.',
    direct_contact_note: 'We contact you directly from Brazil.',
    cta: 'Chat',
    cta_email: 'Send email',
    cta_booking: 'Schedule',
    whatsapp_label: 'BlueAlps WhatsApp',
    email_label: 'BlueAlps Email',
    booking_label: 'Schedule a meeting',
    booking_hint: 'Choose a time through Microsoft Bookings at no cost',
    roles: {
      support: 'Customer Service',
      commercial: 'Sales',
    },
    messages: {
      general: 'Hi! I would like to talk to BlueAlps.',
      emanuele: 'Hi! I would like to talk to Emanuele.',
      commercial: 'Hi! I would like to talk to Sales.',
    },
  },

  responsibleEntity: {
    title: 'Responsible entity',
    address:
      'Hefti Engineering GmbH\nPestalozzistrasse 11\n9400 Rorschach\nSwitzerland',
    email: 'Email:',
  },

  authorizedPersons: {
    title: 'Authorized representatives',
    name: 'Daniel Hefti',
  },

  company: {
    title: 'Company name',
    name: 'Hefti Engineering GmbH',
  },

  registration: {
    title: 'Registration number',
    number: 'CHE-309.856.701',
  },

  disclaimer: {
    title: 'Disclaimer',
    p1: 'The author assumes no liability for the correctness, accuracy, and completeness of the content.',
    p2: 'All offers are non-binding. Changes are reserved.',
  },

  linksDisclaimer: {
    title: 'Disclaimer for external links',
    p1: 'We assume no responsibility for the content of external websites.',
  },

  copyright: {
    title: 'Copyright',
    p1: 'All content on this website is protected by copyright of Hefti Engineering GmbH.',
  },

  source: 'Source: BrainBox Solutions',
};
